import { hasAiReviewColumns } from "../utils/caseReviewFieldState";
import { AI_CONFIDENCE_NOT_PROVIDED_LABEL } from "../constants/caseReviewLabels";
import { useCodingPreview } from '../../meddra/hooks/useCodingDraftPreview';
import { CODING_DRAFT_NOTE } from '../../meddra/model/codingDraftPreview';
import { meddraCodingRenderer, meddraCodingTester } from '../../meddra/renderers/MeddraCodingRenderer';
import { Fragment, useState, type ReactNode } from "react";
import { JsonFormsDispatch, withJsonFormsControlProps, withJsonFormsLayoutProps } from "@jsonforms/react";
import { ControlProps, LayoutProps, Layout, rankWith, isControl, uiTypeIs } from "@jsonforms/core";
import { FollowupFieldChoiceActionEnum } from "@/lib/api/generated/casedb";
import { useFollowupProposals } from "@/features/case_follow_up/review/hooks/followupReviewContext";
import { FollowupBadge } from "@/features/case_follow_up/review/components/FollowupBadge";
import { FollowupProposalPopover } from "@/features/case_follow_up/review/components/FollowupProposalPopover";
import { FOLLOWUP_FIELD_LOCKED_BY_PROPOSAL } from "@/features/case_follow_up/review/constants/followupReviewLabels";
import { CaseReviewContext, useCaseFieldReview, useCaseReviewContext } from "../hooks/useCaseFieldReview";
import { useCaseReviewGroup } from "../hooks/useCaseReviewGroup";
import { CaseFieldReviewTableRow, CaseFieldLabel, CaseFieldErrors } from "../components/field_review/CaseFieldReviewTableRow";
import { CASE_REVIEW_TABLE_HEAD, CASE_REVIEW_TABLE_HEAD_PLAIN } from "../constants/caseReviewTable";
import { AiFieldActions, AiReasoningDisclosure, ConfidencePill, EditedTag, ManualEntryFlag, ProposalDisclosure, ProposalVersionStepper, RevertButton } from "../components/field_review/AiFieldReview";
import { FieldAgentActionButton } from "../components/field_review/FieldAgentActionButton";
import { FIELD_ACTION_NEEDS_EDIT_REASON, FIELD_ACTION_NO_DOCK_REASON } from "../constants/caseReviewLabels";
import { agentActionsOf } from "../model/fieldActionContext";
import { caseFieldDisplayValue, storedCriteriaDisplayValue } from "../utils/caseFieldDisplayValue";
import { isReviewerChange } from "../constants/caseReviewDecisions";
import { FieldHistorySection } from "../components/field_review/FieldHistorySection";
import { CaseFieldInput } from "../components/field_review/CaseFieldInput";
import { useCatalogFieldReview } from "../hooks/useCatalogFieldReview";
import { caseReviewSubsections } from "../utils/caseReviewSubsections";
import { EMPTY_REVIEW_COUNTS } from "../utils/reviewCounts";
import { ReviewCounter } from "../components/review_counts/ReviewCounter";
import { CaseReviewSubsectionTabs } from "../../shell/components/CaseReviewSubsectionTabs";
import { medDraLookupRenderer, medDraLookupTester } from "../../product/renderers/MedDraLookupRenderer";
import { assessmentReferenceRenderer, assessmentReferenceRendererTester } from "../../assessment/renderers/AssessmentReferenceRenderer";
import { imeSeriousnessRenderer, imeSeriousnessTester } from "../../ime_seriousness/renderers/ImeSeriousnessRenderer";
import { CaseReviewIcon } from "../components/field_review/CaseReviewIcon";
import type { CaseFieldValue, CaseReviewContextValue } from "../model/caseReview";
import { isE2bField, isE2bRequiredAndEmpty } from "../utils/catalogE2b";
import { useE2bDeclarationProfilesInView } from "../hooks/e2bDeclarationProfilesContext";
import { LONG_TEXT_SECTION_IDS } from "../constants/caseReviewSections";

/**
 * Whether this table has anything for the AI columns to show.
 *
 * <p>A table of a manually added record has no extracted values at all, and two empty columns on
 * every row of it say that the reviewer forgot something rather than that nothing was extracted.
 */
function hasAiValues(context: CaseReviewContextValue): boolean {
  return hasAiReviewColumns(context.evidence, context.fieldStates);
}

/**
 * One field's row: its label, its AI evidence, its actions and its errors.
 *
 * <p>The input itself is the caller's, because one field is coded against a dictionary rather than
 * a codelist and needs its own control — and everything around that control is the same for it as
 * for the other eighty.
 */
export interface CaseFieldFollowupEdit { value: CaseFieldValue; onChange: (value: CaseFieldValue) => void }

export function CaseFieldRow({ props, field, children }: {
  props: ControlProps;
  field: ReturnType<typeof useCaseFieldReview>;
  children: (editable: boolean, errors: string, followupEdit?: CaseFieldFollowupEdit) => ReactNode;
}) {
  const agentActions = agentActionsOf(props.schema);
  const fieldReview = useCatalogFieldReview({ label: field.label, fieldId: field.fieldId, value: props.data,
    state: field.context?.fieldStates?.[field.fieldId], onSourceHighlight: field.context.requestSourceHighlight,
    agentActions, recordId: field.context.recordId,
    displayValue: agentActions && caseFieldDisplayValue((props.data ?? null) as CaseFieldValue, field.kind, field.options) });
  const [badgeOpen, setBadgeOpen] = useState(false);
  const [proposalOpen, setProposalOpen] = useState(false);
  // Every hook this component calls must run before the visibility check below, so a field that
  // toggles in and out of view (a filter change, a rule) never changes this component's hook order.
  const followup = useFollowupProposals(field.context.recordId, field.fieldId);
  const e2bDeclarationProfiles = useE2bDeclarationProfilesInView();
  if (!field.visible) return null;
  // Validation speaks only while editing; read mode shows what is stored. Client and server
  // findings both arrive through the context, so JSON Forms' own error prop is not read here.
  const errors = props.enabled ? (field.serverError || "") : "";
  // One draft authority per field: a field with a pending proposal changes only through that
  // proposal, never through the ordinary section draft — the backend rejects a field written by
  // both in one request (CatalogFollowupPatchBuilder.conflict).
  const editedProposal = followup?.proposals.find((item) => item.draft?.action === FollowupFieldChoiceActionEnum.Edited);
  const followupLocked = !!followup?.locked && !editedProposal;
  const disabledReason = followupLocked ? FOLLOWUP_FIELD_LOCKED_BY_PROPOSAL : field.disabledReason;
  const editable = !!props.enabled && !disabledReason && !field.displayText;
  const followupEdit: CaseFieldFollowupEdit | undefined = editedProposal && followup
    ? { value: editedProposal.draft?.editedValue ?? null,
        onChange: (value) => followup.actions.choose(editedProposal, FollowupFieldChoiceActionEnum.Edited, value) }
    : undefined;
  const showSource = () => { field.source(); fieldReview.highlightSource(); };
  // The button is disabled with its reason rather than hidden: a reviewer in read mode should learn
  // what unlocks it. Follow-up-locked fields cannot take a proposal either, so they say so too.
  const agentActionDisabledReason = !props.enabled ? FIELD_ACTION_NEEDS_EDIT_REASON
    : !fieldReview.agentAvailable ? FIELD_ACTION_NO_DOCK_REASON : disabledReason;
  return <CaseFieldReviewTableRow selected={field.selected} pendingAi={field.hasExtractionReview && !field.decision} hasError={!!errors} onFocus={field.focus}
    proposalState={field.proposal?.proposal.decision}
    wide={isLongTextField(field.fieldId)}
    label={<CaseFieldLabel inputId={field.inputId} label={field.label} required={field.required} dirty={field.dirty}
      e2b={isE2bField(props.schema)} e2bRequired={isE2bRequiredAndEmpty(props.schema, props.data, e2bDeclarationProfiles)} />}
    confidence={<>
      {field.proposal && <ProposalDisclosure label={field.label} proposal={field.proposal} open={proposalOpen} onOpenChange={setProposalOpen} />}
      {followup && <span className="cr-followup-conf">
        <FollowupBadge proposals={followup.proposals} open={badgeOpen} onOpenChange={setBadgeOpen}>
          <FollowupProposalPopover proposals={followup.proposals} readOnly={followup.actions.readOnly}
            onChoose={followup.actions.choose}
            onEdit={(item, value) => followup.actions.choose(item, FollowupFieldChoiceActionEnum.Edited, value)}
            onShowSource={followup.actions.showSource} onClose={() => setBadgeOpen(false)} />
        </FollowupBadge>
      </span>}
      {field.evidence
        ? <AiReasoningDisclosure
            historySlot={field.context.caseId !== undefined
              ? <FieldHistorySection caseId={field.context.caseId} fieldId={field.fieldId} recordId={field.context.recordId}
                  format={field.kind === "criteria" ? (stored) => storedCriteriaDisplayValue(stored, field.options) : undefined} />
              : undefined}
            label={field.label} evidence={field.evidence} decision={field.decision} canDecide={editable && field.canReview}
            canAccept={field.canAccept} showConfidence={field.showConfidence} hasSource={fieldReview.hasProvenance} onSource={showSource} onAccept={field.accept} onReject={field.reject} onRestoreAiValue={field.restoreAiValue}
            open={fieldReview.reasoningOpen} onOpenChange={fieldReview.setReasoningOpen} />
        : field.hasExtractionReview && !field.decision && field.canAccept
          ? <span className="text-muted-foreground">{AI_CONFIDENCE_NOT_PROVIDED_LABEL}</span>
          : <ConfidencePill decision={field.decision} open={false} />}
      {field.needsManualEntry && !field.evidence && <ManualEntryFlag />}
    </>}
    actions={props.enabled && field.canReview
      ? <AiFieldActions label={field.label} hasReview={field.hasExtractionReview} decision={field.decision} disabled={!editable}
          acceptDisabled={!field.canAccept} proposal={field.proposal}
          onAccept={field.accept} onReject={field.reject} onRestoreAiValue={field.restoreAiValue} onRevert={field.revert} />
      : null}>
    <div className="cr-value-line">
      {field.displayText
        ? <output id={field.inputId} className="cr-read-value">{field.displayText}</output>
        : children(editable, errors, followupEdit)}
      {isReviewerChange(field.decision) && <EditedTag />}
      {editable && field.dirty && <RevertButton label={field.label} onRevert={field.undo} />}
      {editable && field.proposal && <ProposalVersionStepper label={field.label} proposal={field.proposal} />}
      {agentActions && <FieldAgentActionButton actions={agentActions.actions} fieldLabel={field.label}
        disabledReason={agentActionDisabledReason} working={fieldReview.agentWorking} onActivate={fieldReview.activateAgentAction} />}
    </div>
    {props.enabled && disabledReason && <p className="cr-field-note">{disabledReason}</p>}
    {field.valueNote && <p className="cr-field-note">{field.valueNote}</p>}
    <CaseFieldErrors inputId={field.inputId} errors={errors} />
  </CaseFieldReviewTableRow>;
}

function CaseFieldRenderer(props: ControlProps) {
  const field = useCaseFieldReview(props);
  const coding = useCodingPreview();
  const previewed = coding?.values !== undefined && Object.hasOwn(coding.values, props.path);
  const shownProps = previewed ? { ...props, data: coding.values![props.path] } : props;
  const shownField = previewed ? { ...field, disabledReason: coding.loading ? 'Loading coding preview…' : coding.error || CODING_DRAFT_NOTE } : field;
  return <CaseFieldRow props={shownProps} field={shownField}>
    {(editable, errors, followupEdit) => <CaseFieldInput id={field.inputId} kind={field.kind}
      value={followupEdit ? followupEdit.value : (shownProps.data ?? null)}
      options={field.options} disabled={!editable} required={field.required} errors={errors}
      onChange={followupEdit ? followupEdit.onChange : field.update}
      onRequestEdit={shownField.disabledReason ? undefined : field.context.requestEdit} />}
  </CaseFieldRow>;
}

function dispatchChildren(props: LayoutProps, childSlots?: Record<string, ReactNode>) {
  return (props.uischema as Layout).elements.map((element, index) => {
    const scope = (element as { scope?: string }).scope;
    const key = scope ?? `${element.type}-${index}`;
    // A collection the record owns is not a value this form can draw: the scalar schema dropped it,
    // so the slot the page prepared goes here, inside the group the layout put the control in.
    const slot = scope ? childSlots?.[scope] : undefined;
    if (slot !== undefined) return <Fragment key={key}>{slot}</Fragment>;
    return <JsonFormsDispatch key={key} schema={props.schema} uischema={element} path={props.path} enabled={props.enabled} renderers={props.renderers} cells={props.cells} />;
  });
}

/** Does this group hold nothing but a slot? Then its rows would be an empty grid. */
function slotsOnly(props: LayoutProps, childSlots?: Record<string, ReactNode>): boolean {
  const elements = (props.uischema as Layout).elements;
  return elements.length > 0 && elements.every((element) => {
    const scope = (element as { scope?: string }).scope;
    return scope !== undefined && childSlots?.[scope] !== undefined;
  });
}

/**
 * A `Group` is a subsection: one column of field rows.
 *
 * The top-level Group's name is already the navigation tab. Nested Groups are collapsible bands,
 * both in scalar sections and record tables; JSON Forms still evaluates every Group's rules.
 */
function CaseReviewSubsectionRenderer(props: LayoutProps) {
  const { context, label, open, toggle, counts } = useCaseReviewGroup(props);
  if (!props.visible) return null;
  const onlySlots = slotsOnly(props, context.childSlots);
  const body = dispatchChildren(props, context.childSlots);
  const rows = <CaseReviewContext.Provider value={{ ...context, withinSectionGroup: true }}>
    {onlySlots ? <>{body}</> : <div className="cr-rows" role="rowgroup">{body}</div>}
  </CaseReviewContext.Provider>;
  if (!label || (context.groups && !context.withinSectionGroup)) return rows;
  return <div className="cr-group" data-open={open}>
    <button type="button" className="cr-group-head" aria-expanded={open}
      onClick={toggle}>
      <CaseReviewIcon name="chevronDown" className="cr-group-chevron" />
      <span className="cr-group-label">{label}</span>
      <ReviewCounter label={label} counts={counts} />
    </button>
    {open && rows}
  </div>;
}

/** A `HorizontalLayout` inside a subsection adds no structure of its own: its controls are rows of the enclosing column. */
function CaseReviewInlineGroupRenderer(props: LayoutProps) {
  if (!props.visible) return null;
  return <>{dispatchChildren(props)}</>;
}

/**
 * The root `VerticalLayout` is the section: it lists its Groups as tabs, draws the table head,
 * and renders the active Group. Which Group is active comes from the case review context, so the
 * rail and the tabs cannot disagree. Without navigation in the context (a record's detail layout)
 * it is a plain column.
 */
function CaseFieldReviewTableRenderer(props: LayoutProps) {
  const context = useCaseReviewContext();
  if (!props.visible) return null;
  if (!context.groups || context.withinSectionGroup) return <CaseReviewSubsectionRenderer {...props} />;
  const sectionLabel = String(props.schema.title ?? "Section");
  // The page's outline (authored layout) may hold record-list groups this scalar layout does not;
  // those tabs still show, and the page renders the records below the form.
  const outline = caseReviewSubsections(props.uischema, sectionLabel);
  const groups = context.groups ?? outline;
  const activeKey = groups.some((group) => group.key === context.activeGroup) ? context.activeGroup : groups[0]?.key;
  const active = outline.find((group) => group.key === activeKey);
  const tabs = groups.map((group) => ({ key: group.key, label: group.label, counts: context.groupCounts?.[group.key] ?? context.countsFor?.(group.fieldKeys) ?? EMPTY_REVIEW_COUNTS }));
  return <div className="cr-section" role="table" aria-label={`${sectionLabel} fields`} data-ai={hasAiValues(context)}>
    {context.setActiveGroup && <CaseReviewSubsectionTabs tabs={tabs} active={activeKey ?? ""} onSelect={context.setActiveGroup} />}
    {active && <>
      <div className="cr-table-head" role="row">{(hasAiValues(context) ? CASE_REVIEW_TABLE_HEAD : CASE_REVIEW_TABLE_HEAD_PLAIN).map((head) => <span key={head} role="columnheader">{head}</span>)}</div>
      <JsonFormsDispatch key={active.key} schema={props.schema} uischema={active.element} path={props.path} enabled={props.enabled} renderers={props.renderers} cells={props.cells} />
    </>}
  </div>;
}

export const caseReviewRenderers = [
  { tester: rankWith(10, isControl), renderer: withJsonFormsControlProps(CaseFieldRenderer) },
  // Ranked above the ordinary control so only the one field the catalog marks for it is affected.
  { tester: meddraCodingTester, renderer: meddraCodingRenderer },
  { tester: medDraLookupTester, renderer: medDraLookupRenderer },
  { tester: assessmentReferenceRendererTester, renderer: assessmentReferenceRenderer },
  { tester: imeSeriousnessTester, renderer: imeSeriousnessRenderer },
  { tester: rankWith(10, uiTypeIs("VerticalLayout")), renderer: withJsonFormsLayoutProps(CaseFieldReviewTableRenderer) },
  { tester: rankWith(10, uiTypeIs("HorizontalLayout")), renderer: withJsonFormsLayoutProps(CaseReviewInlineGroupRenderer) },
  { tester: rankWith(10, uiTypeIs("Group")), renderer: withJsonFormsLayoutProps(CaseReviewSubsectionRenderer) },
];

/** A text field of a long-prose section (the narrative): drawn full width and tall. */
function isLongTextField(fieldId: string | undefined): boolean {
  return fieldId !== undefined && [...LONG_TEXT_SECTION_IDS].some((sectionId) => fieldId.startsWith(`${sectionId}.`));
}
